import { FC, useMemo } from 'react';
import { ItemList, Thing, VideoObject, WithContext, CollectionPage } from 'schema-dts';
import { useAtomValue } from 'jotai';
import { $CurrentAvailableDiscoverDataOrNull, $CurrentDiscoverKeyword } from '@discover/shared/atom';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { ArticleDetailUtil, CommentUtil, EArticleClass, UserDetailUtil } from 'shared/models';
import { useTranslation } from 'react-i18next';
import { Region } from '@lemon8/web-app-shared/i18n';
import { DynamicHead } from '@lemon8/web-app-shared/ssr';
import { loadable } from 'jotai/utils';

export const JsonLDV2: FC = () => {
  const keyword = useAtomValue($CurrentDiscoverKeyword);
  const { t } = useTranslation();
  const { region } = useAtomValue($Locale);
  const loadableDiscoverData = useAtomValue(loadable($CurrentAvailableDiscoverDataOrNull));
  const abInfo = {};

  if (loadableDiscoverData.state !== 'hasData') {
    return null;
  }

  let discoverData = loadableDiscoverData.data;

  if (!discoverData) {
    return null;
  }

  const itemList: Thing[] = discoverData.article_v2.items.map(({ article, author, comments }, index) => {
    const authorJsonLD = UserDetailUtil.JsonLD.getPersonJsonLD({
      user: author,
      userRegion: article.articleRegion as Region,
    });
    const commentsJsonLD = CommentUtil.JsonLd.getCommentsJsonLd({ comments, region: article.articleRegion as Region });

    if (article.articleClass === EArticleClass.VIDEO) {
      const videoObject: VideoObject = ArticleDetailUtil.JsonLD.getVideoObjectJsonLD({
        article,
        region,
        t,
        abInfo,
      });

      videoObject.position = index + 1;
      videoObject.creator = authorJsonLD;
      videoObject.comment = commentsJsonLD;

      return videoObject;
    } else {
      const socialMediaPosting = ArticleDetailUtil.JsonLD.getSocialMediaJsonLD({ article, region, t, abInfo });

      socialMediaPosting.position = index + 1;
      socialMediaPosting.author = authorJsonLD;
      socialMediaPosting.comment = commentsJsonLD;

      return socialMediaPosting;
    }
  });

  const itemListJSONLD: WithContext<ItemList> = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    mainEntityOfPage: {
      '@id': `https://www.lemon8-app.com/discover/${keyword}?region=${region}`,
      '@type': 'CollectionPage',
    },
    itemListElement: itemList,
    numberOfItems: itemList.length || 0,
  };

  return (
    <DynamicHead>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJSONLD) }}
        id="item-list"
      />
    </DynamicHead>
  );
};
